import React, { useState } from "react";
import PropTypes from "prop-types";
import { ButtonToolbar, Col, Row } from "reactstrap";
import { reduxForm } from 'redux-form';
import { Button, Table, TableBody, TableCell, TableRow, Radio, FormControlLabel, Box, Typography, Paper, Divider, Avatar } from '@mui/material';
import PndRestApi from "src/utils/pnd/PndRestApi";
import { showNotification, successNofication } from "src/shared/components/Notification";
import { Redirect } from 'react-router-dom';
import { RIDER_AVAILABILITY_STATUS_ON_DUTY, RIDER_AVAILABILITY_STATUS_OFF_DUTY, RIDER_AVAILABILITY_STATUS_ON_VACATION, RIDER_STATUS_DELIVERED_FREED, ERROR_MESSAGES, PAGE_TIMEOUT } from '../../../utils/pnd/PndConstants';
import SaveIcon from '@mui/icons-material/Save';
import PersonIcon from '@mui/icons-material/Person';

const GroupUpdateAvaiabilityStatus = ({ dataInfo }) => {

    const [statuses, setStatuses] = useState(() => {
        const initialStatus = {};
        dataInfo.forEach(rider => {
            initialStatus[rider.riderId] = {
                initial: rider.riderAvailabilityId || RIDER_AVAILABILITY_STATUS_ON_DUTY,
                updated: rider.riderAvailabilityId || RIDER_AVAILABILITY_STATUS_ON_DUTY
            };
        });
        return initialStatus;
    });

    const [isRedirect, setIsRedirect] = useState(false);

    if (isRedirect) {
        window.location.reload();
        return <Redirect to="/web_app/rider/availability" />;
    }

    const handleStatusChange = (riderId, riderStatus, event) => {
        if (riderStatus !== RIDER_STATUS_DELIVERED_FREED) {
            showNotification(
                "theme-light",
                "ltr",
                `Info`,
                `You cannot change the status of a rider because he is currently engaged in Order Processing`
            );
            return;
        }
        setStatuses(prevState => ({
            ...prevState,
            [riderId]: {
                ...prevState[riderId],
                updated: Number(event.target.value)
            }
        }));
    };

    const handleSubmit = async () => {
        const changedRecords = dataInfo.filter(rider =>
            statuses[rider.riderId].initial !== statuses[rider.riderId].updated
        ).map(rider => ({
            riderId: rider.riderId,
            availabilityStatusId: statuses[rider.riderId].updated
        }));

        if (changedRecords.length === 0) {
            showNotification("theme-light", "ltr", "Info", "No changes to update.");
            return;
        }

        try {
            const response = await PndRestApi.GroupUpdateRiderAvailabilityStatus(changedRecords);
            if (response.status === 200 && response.data.status === 200) {
                successNofication("theme-light", "ltr", "Success", "Successfully updated the Availability Details");
                setTimeout(() => {
                    setIsRedirect(true);
                }, PAGE_TIMEOUT);
            } else {
                showNotification("theme-light", "ltr", "Error", "Failed to update Availability Details");
            }
        } catch (error) {
            showNotification("theme-light", "ltr", "Error", ERROR_MESSAGES.tryCatchError);
        }
    };

    return (
        <Paper elevation={0} className="p-3">
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, color: 'primary.main' }}>
                <PersonIcon sx={{ mr: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 700 }}>Rider Bulk Status Update</Typography>
            </Box>

            <Divider sx={{ mb: 3 }} />

            <div style={{ maxHeight: '400px', overflowY: 'auto', paddingRight: '8px' }}>
                <Table size="small">
                    <TableBody>
                        {dataInfo.map((rider) => (
                            <TableRow key={rider.riderId} sx={{ '&:hover': { bgcolor: '#f8f9fa' } }}>
                                <TableCell sx={{ borderBottom: '1px solid #eee', py: 1.5 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Avatar sx={{ width: 28, height: 28, bgcolor: 'primary.light', fontSize: '0.8rem', mr: 1.5 }}>
                                            {rider.riderName?.charAt(0)}
                                        </Avatar>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{rider.riderName}</Typography>
                                    </Box>
                                </TableCell>
                                <TableCell sx={{ borderBottom: '1px solid #eee' }}>
                                    <FormControlLabel
                                        control={<Radio size="small" color="success" />}
                                        label={<Typography variant="caption" sx={{ fontWeight: 600 }}>ON DUTY</Typography>}
                                        value={RIDER_AVAILABILITY_STATUS_ON_DUTY}
                                        checked={statuses[rider.riderId].updated === RIDER_AVAILABILITY_STATUS_ON_DUTY}
                                        onChange={(e) => handleStatusChange(rider.riderId, rider.riderStatusId, e)}
                                    />
                                </TableCell>
                                <TableCell sx={{ borderBottom: '1px solid #eee' }}>
                                    <FormControlLabel
                                        control={<Radio size="small" color="warning" />}
                                        label={<Typography variant="caption" sx={{ fontWeight: 600 }}>OFF DUTY</Typography>}
                                        value={RIDER_AVAILABILITY_STATUS_OFF_DUTY}
                                        checked={statuses[rider.riderId].updated === RIDER_AVAILABILITY_STATUS_OFF_DUTY}
                                        onChange={(e) => handleStatusChange(rider.riderId, rider.riderStatusId, e)}
                                    />
                                </TableCell>
                                <TableCell sx={{ borderBottom: '1px solid #eee' }}>
                                    <FormControlLabel
                                        control={<Radio size="small" color="error" />}
                                        label={<Typography variant="caption" sx={{ fontWeight: 600 }}>LEAVE</Typography>}
                                        value={RIDER_AVAILABILITY_STATUS_ON_VACATION}
                                        checked={statuses[rider.riderId].updated === RIDER_AVAILABILITY_STATUS_ON_VACATION}
                                        onChange={(e) => handleStatusChange(rider.riderId, rider.riderStatusId, e)}
                                    />
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>

            <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={handleSubmit}
                    startIcon={<SaveIcon sx={{ fontSize: '0.75rem !important' }} />}
                    sx={{
                        borderRadius: '3px',
                        textTransform: 'none',
                        fontWeight: 600,
                        padding: '2px 8px',
                        fontSize: '10px',
                        minWidth: 'auto',
                        height: '22px',
                        lineHeight: '1',
                        background: 'linear-gradient(90deg, #11998e 0%, #38ef7d 100%)',
                        border: 'none',
                        '&:hover': {
                            boxShadow: '0 1px 3px rgba(17, 153, 142, 0.2)'
                        }
                    }}
                >
                    Apply Bulk Changes
                </Button>
            </Box>
        </Paper>
    );
};

GroupUpdateAvaiabilityStatus.propTypes = {
    dataInfo: PropTypes.array.isRequired,
};

export default reduxForm({ form: 'GroupUpdateAvaiabilityStatusForm', enableReinitialize: true })(GroupUpdateAvaiabilityStatus);
